import { Building, CircleUser } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageLayout } from '../../components/Layout/PageLayout';
import UserProfile from '../../components/Settings/UserProfile';
import ProfilePreferences from '../../components/Settings/ProfilePreferences';
import { Organization } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import organizationService from '../../services/organizationService';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [org, setOrg] = useState<Organization | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(false);

  useEffect(() => {
    const loadOrganization = async () => {
      try {
        setLoadingOrg(true);
        if (user?.organization_id) {
          const orgData = await organizationService.getMyOrganization();
          setOrg(orgData);
        } else {
          setOrg(null);
        }
      } catch (e) {
        console.error('Error loading organization:', e);
        setOrg(null);
      } finally {
        setLoadingOrg(false);
      }
    };

    if (user) {
      loadOrganization();
    }
  }, [user]);

  return (
    <PageLayout
      icon={<CircleUser className="h-6 w-6" />}
      title={user?.name || user?.email || t('profilePage.title')}
      subtitle={user?.email || ''}
    >
      <UserProfile />

      <ProfilePreferences />

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="p-5 border-b border-border bg-muted/50">
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <Building className="h-5 w-5 mr-2 text-primary" />
            {t('profilePage.organizationTitle')}
          </h2>
        </div>
        <div className="p-5 space-y-3">
          {loadingOrg ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
              {t('profilePage.organizationLoading')}
            </div>
          ) : !org ? (
            <div className="text-sm text-muted-foreground">
              {t('profilePage.noOrganization')}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium text-foreground">{org.name}</div>
                  {org.domain && (
                    <div className="text-xs text-muted-foreground">{org.domain}</div>
                  )}
                </div>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  org.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                {org.is_active
                  ? t('profilePage.organizationActive')
                  : t('profilePage.organizationInactive')}
              </span>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default ProfilePage;


